/* ============================================================
   Auth config — the SHAPE of what a front end passes in, plus the pure helpers
   that derive the rest. Reading the values (env vars, app.json) is the front
   end's job: Expo inlines `EXPO_PUBLIC_*`, Vite inlines `VITE_*`, and the core
   should not know which. See app/src/auth/config.ts.
   ============================================================ */

export interface CognitoConfig {
  region: string;
  userPoolId: string;
  clientId: string;
  /** Hosted UI base, e.g. https://<prefix>.auth.<region>.amazoncognito.com,
     with no trailing slash */
  domain: string;
  /* `aws.cognito.signin.user.admin` is not an admin capability — it authorizes
     an access token to act on its OWN user, and `DeleteUser` (the identity half
     of account deletion) refuses a token without it. Email/password tokens come
     from InitiateAuth, which includes it implicitly; the hosted UI grants only
     what is requested here, so omitting it makes in-app deletion impossible for
     social sign-ins — the one flow App Review is required to test. Must stay in
     step with `allowed_oauth_scopes` in infra/cognito.tf. NOTE: a scope is fixed
     at sign-in, so a session opened before this shipped keeps the old set even
     across a refresh — sign out and back in. */
  scopes: string[];
}

/* ---------- social identity providers ----------
   Cognito's own name for each IdP, which is what /oauth2/authorize wants in its
   `identity_provider` parameter. These strings are fixed by Cognito for the
   built-in providers — they must match `supported_identity_providers` on the
   app client in infra/cognito.tf exactly. */
export const IDP = {
  apple: 'SignInWithApple',
  google: 'Google',
  facebook: 'Facebook',
} as const;

export type SocialProvider = keyof typeof IDP;

const ALL_PROVIDERS: SocialProvider[] = ['apple', 'google', 'facebook'];

/** Which social buttons a sign-in screen shows, from a comma-separated list
   (the Terraform output naming the IdPs actually attached to the pool), so a
   button can never appear for a provider that isn't wired up — tapping one of
   those would bounce the user to a Cognito error page. Unset = none.

   ⚠️ Sorted into `ALL_PROVIDERS` order — Apple FIRST — rather than kept in the
   order the string happens to list them. That string is written by
   `infra/deploy.sh` from a Terraform output, so without this the button order
   on the sign-in screen is decided by however Terraform serialised a list.

   Guideline 4.8 does not require Apple to be first, but it does require Sign
   in with Apple to be no less prominent than the alternatives, and "Google
   drew above Apple because of an infra output" is not a position worth
   defending to a reviewer. Making it a property of THIS list means the order
   is the app's decision and cannot drift when the IdP set changes. */
export function parseSocialProviders(raw: string | undefined): SocialProvider[] {
  return (raw ?? '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s): s is SocialProvider => (ALL_PROVIDERS as string[]).includes(s))
    .sort((a, b) => ALL_PROVIDERS.indexOf(a) - ALL_PROVIDERS.indexOf(b));
}

export interface OAuthDiscovery {
  authorizationEndpoint: string;
  tokenEndpoint: string;
  revocationEndpoint: string;
  userInfoEndpoint: string;
  endSessionEndpoint: string;
}

/* The OAuth2 endpoints live on the Cognito *domain* (Hosted UI), NOT on the
   cognito-idp issuer host — that host serves token validation/JWKS but does not
   host the authorize UI. So the discovery document is built by hand rather than
   fetched from the issuer. */
export function discoveryFor(domain: string): OAuthDiscovery {
  const base = domain.replace(/\/+$/, '');
  return {
    authorizationEndpoint: `${base}/oauth2/authorize`,
    tokenEndpoint: `${base}/oauth2/token`,
    revocationEndpoint: `${base}/oauth2/revoke`,
    userInfoEndpoint: `${base}/oauth2/userInfo`,
    endSessionEndpoint: `${base}/logout`,
  };
}
